import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CATEGORY_NOT_LEAD_ASSIGNED,
  TEAM_NOT_FULLY_ON,
  type AcknowledgedGrantImpact,
  type GeneralActivitiesGridResponse,
  type GeneralActivityGrantImpact,
} from '@time-tracking/shared';

import { ApiError } from '../../../api/client.js';
import {
  GrantImpactChangedError,
  invalidateGrantDerived,
  useGeneralActivityImpact,
  useSetGeneralActivityPerson,
  useSetGeneralActivityTeam,
} from '../../../api/general-activities.js';
import type { ToastKind } from '../../../components/useToasts.js';
import type { PersonCellTarget, TeamCellTarget } from './ActivityMatrix.js';
import {
  acknowledgementOf,
  addedToast,
  personRemovedToast,
  teamOnToast,
  teamRemovedToast,
} from './grant-copy.js';
import { gridGroups, NO_TEAM_GROUP_ID, personCellKey, teamCellKey } from './model.js';

export type GrantToast = (message: string, kind: ToastKind) => void;

/** The confirm dialog open over the grid, if any, and where focus goes back to. */
export type GrantDialogState =
  | {
      kind: 'unassignPerson';
      target: PersonCellTarget;
      preview: GeneralActivityGrantImpact;
      changed: boolean;
      saving: boolean;
    }
  | {
      kind: 'unassignTeam';
      target: TeamCellTarget;
      /** Null while "Checking who this affects…". */
      preview: GeneralActivityGrantImpact | null;
      failed: boolean;
      changed: boolean;
      saving: boolean;
      /** Which preview request this dialog is waiting for; a stale one is dropped. */
      request: number;
    }
  | {
      kind: 'turnOn';
      target: TeamCellTarget;
      preview: GeneralActivityGrantImpact;
      changed: boolean;
      saving: boolean;
    };

const SAVING = 'Saving…';
const SAVED = 'Saved.';
const NOT_SAVED = 'Not saved.';
const CHECK_FAILED = 'Couldn’t check who this affects. Nothing was changed.';

/** End a server message with a full stop, so "… Nothing was changed." reads as two sentences. */
const sentence = (s: string): string => (/[.!?…]$/.test(s) ? s : `${s}.`);

/**
 * General activities' grant writes, end to end: which toggle needs a preview or
 * a confirm (R4–R6), the writes themselves, the dialogs' state, pending cells,
 * toasts, and focus back to the originating cell when a dialog closes.
 *
 * There is no optimistic update anywhere. A cell is busy from the click until
 * its write settles; every write — failed ones too — invalidates the grid, so
 * what the boxes show afterwards is always the server's answer.
 */
export function useGrantWrites({
  grid,
  onToast,
  focusCell,
}: {
  grid: GeneralActivitiesGridResponse | undefined;
  onToast: GrantToast;
  focusCell: (cellKey: string) => void;
}) {
  const queryClient = useQueryClient();
  const personWrite = useSetGeneralActivityPerson();
  const teamWrite = useSetGeneralActivityTeam();
  const impact = useGeneralActivityImpact();

  const [pending, setPending] = useState<ReadonlyMap<string, readonly string[]>>(new Map());
  const [dialog, setDialog] = useState<GrantDialogState | null>(null);
  const [status, setStatus] = useState('');
  const [returnFocusTo, setReturnFocusTo] = useState<string | null>(null);
  const requestSeq = useRef(0);
  /**
   * Writes and previews in flight, counted synchronously (state would lag a
   * render). A new toggle is ignored while any is pending or a dialog is up,
   * so a late preview can never open over — or replace — another decision.
   */
  const inflight = useRef(0);

  // Focus goes back to the cell once the dialog has unmounted (and its focus
  // trap has restored whatever it restores), never while it is still up.
  useEffect(() => {
    if (dialog === null && returnFocusTo !== null) {
      focusCell(returnFocusTo);
      setReturnFocusTo(null);
    }
  }, [dialog, returnFocusTo, focusCell]);

  const closeDialog = useCallback((cellKey: string) => {
    setDialog(null);
    setReturnFocusTo(cellKey);
  }, []);

  // ── Pending cells ──────────────────────────────────────────────────────────

  const begin = (origin: string, keys: readonly string[]) => {
    inflight.current += 1;
    setPending((prev) => new Map(prev).set(origin, keys));
  };
  const end = (origin: string) => {
    inflight.current = Math.max(0, inflight.current - 1);
    setPending((prev) => {
      const next = new Map(prev);
      next.delete(origin);
      return next;
    });
  };

  /** A toggle starts only from rest: no dialog open, nothing in flight. */
  const atRest = () => dialog === null && inflight.current === 0;

  /** A two-team person appears under each team: all their copies of the cell are busy. */
  const personKeys = (t: PersonCellTarget): string[] => {
    const keys = grid
      ? gridGroups(grid)
          .filter((g) => g.members.some((m) => m.userId === t.member.userId))
          .map((g) => personCellKey(g.id, t.member.userId, t.activity.categoryId))
      : [];
    return keys.includes(t.cellKey) ? keys : [...keys, t.cellKey];
  };
  /** A team write disables the team's heading and every member cell under it. */
  const teamKeys = (t: TeamCellTarget): string[] => [
    teamCellKey(t.team.teamId, t.activity.categoryId),
    ...t.team.members.map((m) => personCellKey(t.team.teamId, m.userId, t.activity.categoryId)),
  ];

  // ── Errors ─────────────────────────────────────────────────────────────────

  const reportError = (err: unknown, activity: string, team?: string) => {
    setStatus(NOT_SAVED);
    const code = err instanceof ApiError ? err.code : undefined;
    if (code === CATEGORY_NOT_LEAD_ASSIGNED) {
      onToast(
        `${activity} is no longer assigned by leads, so nothing was changed. The list has been refreshed.`,
        'error',
      );
    } else if (code === TEAM_NOT_FULLY_ON) {
      onToast(
        `${team ?? 'This team'} changed while you were looking, so nothing was changed. Check ${activity} and try again.`,
        'warn',
      );
    } else if (err instanceof ApiError && err.status === 503) {
      onToast(
        `Someone else is changing ${activity} right now, so nothing was saved. Try again in a moment.`,
        'error',
      );
    } else {
      const message =
        err instanceof ApiError && err.apiMessage
          ? err.apiMessage
          : 'Something went wrong reaching the server';
      onToast(`${sentence(message)} Nothing was changed.`, 'error');
    }
  };

  // ── Person ─────────────────────────────────────────────────────────────────

  const writePerson = async (
    t: PersonCellTarget,
    granted: boolean,
    acknowledged?: AcknowledgedGrantImpact,
  ) => {
    const activity = t.activity.name;
    const name = t.member.displayName;
    begin(t.cellKey, personKeys(t));
    setStatus(SAVING);
    try {
      const result = await personWrite.mutateAsync({
        categoryId: t.activity.categoryId,
        userId: t.member.userId,
        granted,
        ...(acknowledged ? { acknowledged } : {}),
      });
      setStatus(SAVED);
      if (acknowledged) closeDialog(t.cellKey);
      onToast(
        granted ? addedToast(activity, name) : personRemovedToast(activity, name, result.impact),
        granted ? 'success' : 'neutral',
      );
    } catch (err) {
      if (acknowledged && err instanceof GrantImpactChangedError) {
        setStatus('');
        setDialog({
          kind: 'unassignPerson',
          target: t,
          preview: err.preview,
          changed: true,
          saving: false,
        });
      } else {
        if (acknowledged) closeDialog(t.cellKey);
        reportError(err, activity);
      }
    } finally {
      end(t.cellKey);
    }
  };

  const onTogglePerson = (t: PersonCellTarget) => {
    if (!atRest()) return;
    if (!t.cell.granted) {
      void writePerson(t, true);
      return;
    }
    // An untick applies at once only when nobody's row stays and no other team
    // of theirs is involved — which only the server knows. Check first; the
    // cell is busy meanwhile, so the decision is made on one click.
    begin(t.cellKey, personKeys(t));
    setStatus('Checking who this affects…');
    void impact.person({ categoryId: t.activity.categoryId, userId: t.member.userId }).then(
      (preview) => {
        end(t.cellKey);
        setStatus('');
        const ownTeamId = t.groupId === NO_TEAM_GROUP_ID ? null : t.groupId;
        const elsewhere = preview.alsoOnTeams.some((team) => team.teamId !== ownTeamId);
        if (preview.keptWithHours.length === 0 && !elsewhere) {
          void writePerson(t, false);
        } else {
          const next: GrantDialogState = {
            kind: 'unassignPerson',
            target: t,
            preview,
            changed: false,
            saving: false,
          };
          // Never replace a dialog that is already open.
          setDialog((d) => d ?? next);
        }
      },
      () => {
        end(t.cellKey);
        setStatus(NOT_SAVED);
        onToast(CHECK_FAILED, 'error');
        invalidateGrantDerived(queryClient);
      },
    );
  };

  // ── Team ───────────────────────────────────────────────────────────────────

  const turnTeamOn = async (t: TeamCellTarget, acknowledged?: AcknowledgedGrantImpact) => {
    const activity = t.activity.name;
    const team = t.team.name;
    let resendUnacknowledged = false;
    begin(t.cellKey, teamKeys(t));
    setStatus(SAVING);
    try {
      const result = await teamWrite.mutateAsync({
        categoryId: t.activity.categoryId,
        teamId: t.team.teamId,
        wholeTeam: true,
        ...(acknowledged ? { acknowledged } : {}),
      });
      setStatus(SAVED);
      if (acknowledged) closeDialog(t.cellKey);
      const overridden = result.impact.overridden;
      // The override toast is a warning, so it lingers: it is the only place
      // the removals another lead made are named once they are gone.
      onToast(teamOnToast(activity, team, overridden), overridden.length > 0 ? 'warn' : 'success');
    } catch (err) {
      if (err instanceof GrantImpactChangedError) {
        setStatus('');
        if (err.preview.overridden.length === 0) {
          // Nobody's removal is in the way any more: nothing left to confirm.
          // (Unacknowledged, the server never sends this 409 with an empty list;
          // resending then could loop, so it is reported instead.)
          if (acknowledged) closeDialog(t.cellKey);
          else reportError(err, activity, team);
          resendUnacknowledged = acknowledged !== undefined;
        } else {
          // Unacknowledged, this 409 is the override confirmation's prompt;
          // acknowledged, the list changed under the open dialog.
          setDialog({
            kind: 'turnOn',
            target: t,
            preview: err.preview,
            changed: acknowledged !== undefined,
            saving: false,
          });
        }
      } else {
        if (acknowledged) closeDialog(t.cellKey);
        reportError(err, activity, team);
      }
    } finally {
      end(t.cellKey);
    }
    if (resendUnacknowledged) await turnTeamOn(t);
  };

  const loadTeamImpact = (t: TeamCellTarget, request: number) => {
    void impact.team({ categoryId: t.activity.categoryId, teamId: t.team.teamId }).then(
      (preview) =>
        setDialog((d) =>
          d?.kind === 'unassignTeam' && d.request === request ? { ...d, preview } : d,
        ),
      () =>
        setDialog((d) =>
          d?.kind === 'unassignTeam' && d.request === request ? { ...d, failed: true } : d,
        ),
    );
  };

  const openTeamOff = (t: TeamCellTarget) => {
    const request = ++requestSeq.current;
    setDialog({
      kind: 'unassignTeam',
      target: t,
      preview: null,
      failed: false,
      changed: false,
      saving: false,
      request,
    });
    loadTeamImpact(t, request);
  };

  const retryTeamImpact = () => {
    if (dialog?.kind !== 'unassignTeam') return;
    const request = ++requestSeq.current;
    setDialog({ ...dialog, failed: false, preview: null, request });
    loadTeamImpact(dialog.target, request);
  };

  const turnTeamOff = async (t: TeamCellTarget, preview: GeneralActivityGrantImpact) => {
    const activity = t.activity.name;
    const team = t.team.name;
    begin(t.cellKey, teamKeys(t));
    setStatus(SAVING);
    try {
      const result = await teamWrite.mutateAsync({
        categoryId: t.activity.categoryId,
        teamId: t.team.teamId,
        wholeTeam: false,
        acknowledged: acknowledgementOf(preview),
      });
      setStatus(SAVED);
      closeDialog(t.cellKey);
      onToast(teamRemovedToast(activity, team, result.impact), 'neutral');
    } catch (err) {
      if (err instanceof GrantImpactChangedError) {
        setStatus('');
        setDialog((d) =>
          d?.kind === 'unassignTeam' && d.target.cellKey === t.cellKey
            ? { ...d, preview: err.preview, changed: true, saving: false }
            : d,
        );
      } else {
        closeDialog(t.cellKey);
        reportError(err, activity, team);
      }
    } finally {
      end(t.cellKey);
    }
  };

  const onToggleTeam = (t: TeamCellTarget) => {
    if (!atRest()) return;
    // Only a fully-on heading offers Off; a mixed or off one turns the team on.
    if (t.state === 'on') openTeamOff(t);
    else void turnTeamOn(t);
  };

  // ── Dialog actions ─────────────────────────────────────────────────────────

  const cancelDialog = () => {
    if (dialog) closeDialog(dialog.target.cellKey);
  };

  const confirmDialog = () => {
    if (!dialog || dialog.saving) return;
    if (dialog.kind === 'unassignTeam') {
      if (!dialog.preview) return;
      setDialog({ ...dialog, saving: true });
      void turnTeamOff(dialog.target, dialog.preview);
    } else if (dialog.kind === 'unassignPerson') {
      setDialog({ ...dialog, saving: true });
      void writePerson(dialog.target, false, acknowledgementOf(dialog.preview));
    } else {
      setDialog({ ...dialog, saving: true });
      void turnTeamOn(dialog.target, acknowledgementOf(dialog.preview));
    }
  };

  const busyKeys = new Set<string>();
  for (const keys of pending.values()) for (const k of keys) busyKeys.add(k);
  const spinnerKeys = new Set(pending.keys());

  return {
    busyKeys,
    spinnerKeys,
    status,
    dialog,
    onTogglePerson,
    onToggleTeam,
    cancelDialog,
    confirmDialog,
    retryTeamImpact,
  };
}
